/* eslint-disable nimbus/prefer-until-destroyed */
import { Injectable } from '@angular/core';
import { HubConnectionState } from '@microsoft/signalr';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';
import { IUserTasksCapacity } from '@nimbus-global/features/user-tasks-capacity/models/user-tasks-capacity.model';
import { IUserSessionLive } from '@nimbus-global/features/reportingLiveData/models/user-session.model';
import { UserResponsibilityProfile } from '../../models/client-settings';
import { AuthService } from '../auth.service';
import { MessageService } from '../message.service';
import { SettingsService } from '../settings.service';
import { GraphPresence, NimbusPresence } from '../../models/presence.model';
import { LoggingService } from '../logging.service';
import { AvailabilityInfo } from '../../interfaces/availability-info';
import { UserSettingsService } from '../user-settings.service';
import { BaseAssistantHub } from './base-assistant.hub';

@Injectable({
  providedIn: 'root',
})
export class AssistantHub extends BaseAssistantHub {
  private keyExtendAfterCallWork: string = 'ExtendAfterCallWork';
  private keyStopAfterCallWork: string = 'StopAfterCallWork';
  private keySetResponsibilityProfile: string = 'SetResponsibilityProfile';
  private keyGetResponsibilityProfiles: string = 'GetResponsibilityProfiles';
  private keyStopRona: string = 'StopRona';
  private keyGetAvailabilityState: string = 'GetAvailabilityState';
  private keyGetTaskCapacity: string = 'GetTaskCapacity';
  private keyUserCapacityChanged: string = 'UserCapacityChanged';
  private keyLiveDataUserSessionChanged: string = 'LiveDataUserSessionChanged';
  private keyUserProfileListChanged: string = 'UserProfileListChanged';
  private keyUserAvailabilityChanged: string = 'UserAvailabilityChanged';
  private keyUserPresenceChanged: string = 'UserPresenceChanged';
  private keyFeaturesChanged: string = 'FeaturesChanged';

  protected signalRUrl: string = 'api/assistant/v1/hub';

  public taskCapacity$ = new BehaviorSubject<IUserTasksCapacity>(undefined);
  public onLiveDataUserSessionChanged$ = new Subject<IUserSessionLive>();
  public onUserProfileListChanged$ = new Subject<UserResponsibilityProfile[]>();
  public availabilityState$ = new BehaviorSubject<AvailabilityInfo>(undefined);
  public presence$ = new BehaviorSubject<GraphPresence>(undefined);
  public useSignalrPresence$ = new BehaviorSubject<boolean>(false);

  public constructor(
    private readonly authService: AuthService,
    private readonly settingsService: SettingsService,
    protected readonly userSettingsService: UserSettingsService,
    protected readonly loggingService: LoggingService,
  ) {
    super(userSettingsService, loggingService);

    this.isConnected$.pipe(filter((x) => x !== undefined)).subscribe((connected) => {
      if (!connected && this.availabilityState$.value === undefined) {
        this.availabilityState$.next(null);
      }
    });

    const isAllowedToConnect$ = combineLatest([this.authService.authenticated$, this.settingsService.isLicensed$]).pipe(
      map(([isAuthenticated, isLicensed]) => isAuthenticated && isLicensed),
      distinctUntilChanged(),
    );

    const isTimeToRefreshConnection$ = combineLatest([
      this.authService.accessTokenReceived$.pipe(filter((x) => x)),
      this.authService.tokenAquiredByPopup$,
    ]).pipe(
      map((_) => true),
      filter(
        (_) =>
          !this.connection ||
          (this.connection?.state !== HubConnectionState.Connected && this.connection?.state !== HubConnectionState.Connecting),
        // TODO DK: allow refresh in HubConnectionState.Reconnecting state (e.g. case when token expired or smth)
      ),
      startWith(true),
    );

    const isReadyToConnect$ = combineLatest([isAllowedToConnect$, isTimeToRefreshConnection$]).pipe(
      map(([isAllowedToConnect, _]) => isAllowedToConnect),
    );

    this.initialize(isReadyToConnect$);
  }

  public extendAcw(): Observable<void> {
    return this.invokeToObservable(this.keyExtendAfterCallWork);
  }

  public closeAcw(): Observable<void> {
    return this.invokeToObservable(this.keyStopAfterCallWork);
  }

  public setResponsibilityProfile(profile: UserResponsibilityProfile): Observable<void> {
    return this.invokeToObservable(this.keySetResponsibilityProfile, profile);
  }

  public getResponsibilityProfiles(): Observable<UserResponsibilityProfile[]> {
    return this.invokeToObservable(this.keyGetResponsibilityProfiles);
  }

  public resetRona(): Observable<void> {
    return this.invokeToObservable(this.keyStopRona);
  }

  protected onConnected(): void {
    this.invokeToPromise<AvailabilityInfo>(this.keyGetAvailabilityState).then((x) => this.userAvailabilityChanged(x));
    this.invokeToPromise<IUserTasksCapacity>(this.keyGetTaskCapacity).then((x) => this.userCapacityChanged(x));
  }

  protected onConnectedFailed(): void {
    MessageService.SendBringToFrontMessage();
  }

  protected subscribeEvents(): void {
    this.connection.on(this.keyUserCapacityChanged, this.userCapacityChanged.bind(this));
    this.connection.on(this.keyLiveDataUserSessionChanged, this.liveDataUserSessionChanged.bind(this));
    this.connection.on(this.keyUserProfileListChanged, this.userProfileListChanged.bind(this));
    this.connection.on(this.keyUserAvailabilityChanged, this.userAvailabilityChanged.bind(this));
    this.connection.on(this.keyUserPresenceChanged, this.userPresenceChanged.bind(this));
    this.connection.on(this.keyFeaturesChanged, this.featuresChanged.bind(this));
  }

  protected unsubscribeEvents(): void {
    this.availabilityState$.next(undefined);
    this.presence$.next(undefined);
    this.useSignalrPresence$.next(false);
    this.taskCapacity$.next(null);
    this.connection.off(this.keyUserCapacityChanged);
    this.connection.off(this.keyLiveDataUserSessionChanged);
    this.connection.off(this.keyUserProfileListChanged);
    this.connection.off(this.keyUserAvailabilityChanged);
    this.connection.off(this.keyUserPresenceChanged);
    this.connection.off(this.keyFeaturesChanged);
  }

  protected acquireIdToken(): Observable<string> {
    return this.authService.acquireIdToken();
  }

  private userCapacityChanged(data: IUserTasksCapacity): void {
    this.taskCapacity$.next(data);
  }

  private liveDataUserSessionChanged(session: IUserSessionLive): void {
    this.onLiveDataUserSessionChanged$.next(session);
  }

  private userProfileListChanged(profiles: UserResponsibilityProfile[]): void {
    this.onUserProfileListChanged$.next(profiles);
  }

  private userAvailabilityChanged(availability: AvailabilityInfo): void {
    this.availabilityState$.next(availability);
  }

  private userPresenceChanged(presence: NimbusPresence): void {
    this.presence$.next({ availability: presence.presenceStatus, activity: presence.activity });
  }

  private featuresChanged(args: { features: { SignalRPresenceAssistant: boolean } }): void {
    this.useSignalrPresence$.next(args?.features?.SignalRPresenceAssistant);
  }
}
